import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { generateSkuForCategory, nextAvailableSku } from '../../lib/skuGenerator';
import ReactDOM from 'react-dom';
import { supabase } from '../../lib/supabase';
import UnitSelect from '../UnitSelect';
import {
  IconX,
  IconCheck,
  IconAlertTriangle,
  IconSparkles,
} from '@tabler/icons-react';

// UOM_OPTIONS retired at Session 78n2, #398. One of four hardcoded copies of the
// same nine words - this one in a third distinct ORDER - none of which knew about
// the others or about the database enum that actually constrained the column.
// The governed list is now the only source. See components/UnitSelect.js.

export default function CatalogPromotionModal({
  matchedRequest,
  requestItem,     // new Session 74k7 — promote THIS line, not whichever is first
  provisionalVendorItem,
  onClose,
  onPromoted,
  profile,
}) {
  // requestItem is optional and backward compatible. The three ad-hoc receive
  // screens pass nothing and keep the original items[0] behavior; the multi-item
  // receive sheet passes the specific off-catalog line whose link was tapped,
  // because on a multi-item request items[0] is frequently the wrong item.
  const firstItem = requestItem || matchedRequest?.material_request_items?.[0];

  // #422 / M16, Session 78s.1. A THIRD way in, and it has no order behind it.
  // "Add to our catalog" on the vendor's own catalog promotes a row that was
  // IMPORTED rather than bought — so there is no request, no request line and no
  // branch to read one from. Everything below falls back to the vendor row
  // itself, and to the person's own branch for the opening stock row.
  //
  // The database function needs no change for this: its provisional-relink
  // branch keys on the vendor_items id alone and sets `is_provisional = false`
  // without ever testing it, so an already-false row takes the same path. Read
  // out of the function body rather than assumed — M17's claim that promotion
  // stays exactly where it is only holds if that is true.
  const seedDescription = firstItem?.off_catalog_description
    || provisionalVendorItem?.off_catalog_description
    || '';

  const [itemName, setItemName] = useState(seedDescription);
  const [sku, setSku] = useState('');
  const [uom, setUom] = useState(firstItem?.off_catalog_uom || 'each');
  const [categoryId, setCategoryId] = useState('');
  const [categories, setCategories] = useState([]);
  const [vendorId, setVendorId] = useState(provisionalVendorItem?.vendor_id || matchedRequest?.vendor_id || null);
  const [vendors, setVendors] = useState([]);
  const [unitCost, setUnitCost] = useState('');
  // 74p. The vendor's own part number. Pre-filled from the provisional record when
  // purchasing captured it at Mark Ordered, editable here, and written by
  // promote_off_catalog_item() on both its branches - the relink and the fresh insert.
  // Without this the number was captured and then silently dropped at promotion.
  const [vendorItemNumber, setVendorItemNumber] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  // #471, S16. Creation path TWO. The gate names this one explicitly: miss it
  // and the catalog grows items with no code by the one route that runs while
  // somebody is busy receiving an order, which is exactly when nobody stops to
  // invent one. Same behaviour as the Add-item form, deliberately - a person
  // who meets both screens should not have to learn two rules.
  const { skuGenerationEnabled } = useAuth();
  const [skuTouched, setSkuTouched] = useState(false);

  // Pre-fill unit cost from provisional vendor_items row
  useEffect(() => {
    if (provisionalVendorItem?.vendor_price && provisionalVendorItem?.pack_qty) {
      const computed = parseFloat(provisionalVendorItem.vendor_price) / parseInt(provisionalVendorItem.pack_qty);
      setUnitCost(computed.toFixed(4));
    } else if (provisionalVendorItem?.vendor_price) {
      setUnitCost(String(provisionalVendorItem.vendor_price));
    }
    if (provisionalVendorItem?.vendor_id) {
      setVendorId(provisionalVendorItem.vendor_id);
    }
    if (provisionalVendorItem?.vendor_item_number) {
      setVendorItemNumber(provisionalVendorItem.vendor_item_number);
    }
  }, [provisionalVendorItem]);

  useEffect(() => {
    async function loadRefs() {
      const [{ data: cats }, { data: vens }] = await Promise.all([
        supabase.from('inventory_categories').select('id, name, parent_id, is_active, code').order('name'),  // `code` feeds #471's SKU proposal on promotion
        supabase.from('inventory_vendors').select('id, name').eq('is_active', true).order('name'),
      ]);
      setCategories(cats || []);
      setVendors(vens || []);
    }
    loadRefs();
  }, []);

  // #471, callout A. The code fills in while the person is still looking at the
  // form, on the same terms as Add item.
  useEffect(() => {
    if (!skuGenerationEnabled || skuTouched) return;
    const proposed = generateSkuForCategory({ categories, categoryId, name: itemName });
    if (proposed) setSku(prev => (prev === proposed ? prev : proposed));
  }, [itemName, categoryId, categories, skuGenerationEnabled, skuTouched]);

  async function handlePromote() {
    if (!itemName.trim()) { setError('Item name is required.'); return; }
    if (!uom) { setError('UOM is required.'); return; }
    // #262. Required rather than optional, on Colby's call at 74s. The field defaulted to
    // "No category", so the path of least resistance produced an uncategorised item - which
    // is how both of staging's promoted rows ended up bare, on the queue built specifically
    // to improve catalog hygiene. SAP and Coupa both require a material group / commodity
    // code at item creation for the same reason.
    if (!categoryId) { setError('Category is required.'); return; }
    if (!(matchedRequest?.branch_id ?? profile?.branch_id)) {
      setError('Stede does not know which branch to stock this at. Set your branch on your profile, or promote this from the order it came in on.');
      return;
    }
    if (!provisionalVendorItem && !!vendorId !== !!unitCost) {
      setError('Enter both a vendor and a unit cost to save pricing on this item, or clear both to add it with no price yet.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      // #471, S10. Same rule as the Add-item form: only a code WE proposed gets
      // a suffix. A code the person typed is left exactly as typed, and the
      // unique index on inventory_items.sku rejects a true duplicate with an
      // error they can act on.
      let resolvedSku = sku.trim();
      if (!skuTouched && resolvedSku) {
        const { data: clashes } = await supabase
          .from('inventory_items')
          .select('sku')
          .ilike('sku', `${resolvedSku}%`);
        resolvedSku = nextAvailableSku(resolvedSku, (clashes || []).map(r => r.sku));
      }
      const { data: newItemId, error: rpcErr } = await supabase.rpc('promote_off_catalog_item', {
        p_name: itemName.trim(),
        p_sku: resolvedSku || null,
        p_uom: uom,
        p_category_id: categoryId ? parseInt(categoryId) : null,
        p_vendor_id: vendorId || null,
        p_description: seedDescription || null,
        // The branch the opening stock row is created for. An order-born
        // promotion uses the branch that ordered it; an imported row has no
        // order, so it uses the branch of the person doing the promoting.
        // Unguarded until 78s.1, which would have thrown on any caller that
        // passed no request rather than failing with a sentence.
        p_branch_id: matchedRequest?.branch_id ?? profile?.branch_id ?? null,
        p_provisional_vendor_item_id: provisionalVendorItem?.id || null,
        p_unit_cost: unitCost ? parseFloat(unitCost) : null,
        p_request_item_id: requestItem?.id ?? null,
        p_vendor_item_number: vendorItemNumber.trim() || null,
      });

      if (rpcErr) throw rpcErr;

      setDone(true);
      setTimeout(() => {
        onPromoted?.(newItemId);
        onClose();
      }, 1600);
    } catch (e) {
      setError('Could not promote to catalog: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  // #262. inventory_categories is a two-level hierarchy and an item carries ONE
  // category_id, which must point at a subcategory: Colby's rule at 74s is that every
  // item has both a category and a subcategory, and both display values derive from that
  // single leaf - the leaf is the Sub, its parent is the Category. Parents are therefore
  // group headers only and are never selectable. A parent with no children yet shows a
  // disabled placeholder rather than disappearing, so an admin who has just created one
  // can see that it still needs a subcategory before anything can be filed under it.
  // 74u. Deactivated categories drop out of authoring pickers -- that is what
  // deactivating one is for. They deliberately stay in filters and in label
  // resolution, because items already filed on one must still be findable and
  // must still render their category. Same picker/filter split 74s drew.
  const categoryGroups = categories
    .filter(c => !c.parent_id && c.is_active !== false)
    .map(parent => ({
      parent,
      children: categories.filter(c => c.parent_id === parent.id && c.is_active !== false),
    }));
  const selectedVendor = vendors.find(v => v.id === vendorId);

  const modal = (
    <div style={{ position: 'fixed', inset: 0, zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />
      <div style={{
        position: 'relative', background: 'white', borderRadius: 16,
        width: '100%', maxWidth: 560, maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 8px 48px rgba(0,0,0,0.2)',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #F0F0F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconSparkles size={18} color="#EF9F27" />
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0F0F0F' }}>Add to catalog</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <IconX size={18} color="#7A7A7A" />
          </button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px 24px' }}>

          {done ? (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#E8F5E9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <IconCheck size={28} color="#1D9E75" />
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1D9E75', marginBottom: 4 }}>Added to catalog</div>
              <div style={{ fontSize: 13, color: '#7A7A7A' }}>The item is now in the catalog and available for future requests.</div>
            </div>
          ) : (
            <>
              {/* Pre-fill notice */}
              <div style={{ background: '#FFF8EC', border: '1px solid #EF9F27', borderRadius: 10, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <IconSparkles size={14} color="#EF9F27" style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ fontSize: 12, color: '#9A5F00', lineHeight: 1.5 }}>
                  Pre-filled from the request — review and adjust before saving.
                </div>
              </div>

              {error && (
                <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <IconAlertTriangle size={13} /> {error}
                </div>
              )}

              {/* 6-field 2-column grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

                {/* Item name — spans full width */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 5 }}>Item name</div>
                  <input
                    type="text" value={itemName} onChange={e => setItemName(e.target.value)}
                    placeholder="Enter item name"
                    style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 9, padding: '0 12px', color: '#0F0F0F', boxSizing: 'border-box' }}
                  />
                </div>

                {/* SKU */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 5 }}>SKU <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></div>
                  <input
                    type="text" value={sku}
                    onChange={e => { setSkuTouched(true); setSku(e.target.value); }}
                    placeholder="e.g. GRM-VHB-1IN"
                    style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 9, padding: '0 12px', color: '#0F0F0F', boxSizing: 'border-box' }}
                  />
                  {/* Gate callout C. Stated here for the same reason as on Add
                      item: while they can still do something about it. */}
                  {skuGenerationEnabled && !skuTouched && sku && (
                    <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 4 }}>
                      Suggested from the category and name. Edit it now if you want
                      something else &mdash; it is kept as typed and never changes
                      later, even if this item moves category.
                    </div>
                  )}
                </div>

                {/* Vendor item # */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 5 }}>Vendor item # <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></div>
                  <input
                    type="text" value={vendorItemNumber} onChange={e => setVendorItemNumber(e.target.value)}
                    placeholder="e.g. AB-38-SLV"
                    style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 9, padding: '0 12px', color: '#0F0F0F', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', boxSizing: 'border-box' }}
                  />
                  <div style={{ fontSize: 10, color: '#9A9A9A', marginTop: 3, lineHeight: 1.4 }}>
                    Ours is the SKU; this is the vendor's.
                  </div>
                </div>

                {/* UOM */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 5 }}>UOM</div>
                  {/* Stocking units, not package types. The item being created
                      here is counted in this unit forever after; how the vendor
                      shipped it is a separate fact already captured on the
                      provisional vendor row. */}
                  <UnitSelect
                    kind="measure"
                    value={uom}
                    onChange={setUom}
                    placeholder="pick one"
                    ariaLabel="What one of this item is"
                    style={{ borderRadius: 9, padding: '0 12px' }}
                  />
                </div>

                {/* Category */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 5 }}>Category</div>
                  <select
                    value={categoryId} onChange={e => setCategoryId(e.target.value)}
                    style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 9, padding: '0 12px', color: categoryId ? '#0F0F0F' : '#9A9A9A', appearance: 'none', background: 'white', boxSizing: 'border-box' }}
                  >
                    <option value="">Select category</option>
                    {categoryGroups.map(({ parent, children }) => (
                      <optgroup key={parent.id} label={parent.name}>
                        {children.length > 0
                          ? children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)
                          : <option value="" disabled>No subcategories yet</option>}
                      </optgroup>
                    ))}
                  </select>
                </div>

                {/* Vendor */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 5 }}>Vendor <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></div>
                  <select
                    value={vendorId || ''} onChange={e => setVendorId(e.target.value ? parseInt(e.target.value) : null)}
                    style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 9, padding: '0 12px', color: '#0F0F0F', appearance: 'none', background: 'white', boxSizing: 'border-box' }}
                  >
                    <option value="">No vendor</option>
                    {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </div>

                {/* Unit cost */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 5 }}>Unit cost <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></div>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: '#5C5C5C' }}>$</span>
                    <input
                      type="number" step="0.0001" value={unitCost} onChange={e => setUnitCost(e.target.value)}
                      placeholder="0.00"
                      style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 9, padding: '0 12px 0 22px', color: '#0F0F0F', boxSizing: 'border-box' }}
                    />
                  </div>
                  {provisionalVendorItem && (
                    <div style={{ fontSize: 10, color: '#9A9A9A', marginTop: 3 }}>
                      From order: ${parseFloat(provisionalVendorItem.vendor_price || 0).toFixed(2)} / {provisionalVendorItem.pack_qty || 1} {provisionalVendorItem.pack_unit || 'pack'}
                    </div>
                  )}
                </div>

              </div>

              {/* Provisional record note */}
              {provisionalVendorItem && (
                <div style={{ marginTop: 16, background: '#F5F5F3', borderRadius: 8, padding: '10px 12px', fontSize: 11, color: '#7A7A7A', lineHeight: 1.5 }}>
                  A provisional vendor record was created when this item was ordered. Confirming will attach it to the new catalog entry and mark it as the preferred vendor.
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!done && (
          <div style={{ padding: '14px 20px', borderTop: '1px solid #F0F0F0', display: 'flex', gap: 10, flexShrink: 0 }}>
            <button onClick={onClose}
              style={{ flex: 1, padding: '11px 0', background: 'none', border: '1px solid #E0E0E0', borderRadius: 10, fontSize: 14, fontWeight: 600, color: '#5C5C5C', cursor: 'pointer' }}>
              Not now
            </button>
            <button onClick={handlePromote} disabled={saving}
              style={{ flex: 2, padding: '11px 0', background: saving ? '#E0E0E0' : '#1D9E75', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, color: saving ? '#9A9A9A' : 'white', cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Saving...' : 'Add to catalog'}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
